const STYLE_ID = 'card-flip-keyframes';
const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

function injectKeyframes() {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  // --cf-glow is set inline on the wrapper per card-type so the color always matches
  s.textContent = `
    @keyframes cfPulse {
      0%,100% { box-shadow: 0 4px 20px rgba(0,0,0,0.55), 0 0  6px var(--cf-glow, rgba(255,191,229,0.2)); }
      50%      { box-shadow: 0 4px 20px rgba(0,0,0,0.55), 0 0 16px var(--cf-glow, rgba(255,191,229,0.5)); }
    }
    .cf-pulse { animation: cfPulse 1.4s ease-in-out infinite; }
  `;
  document.head.appendChild(s);
}

const BACK_BG = {
  truth:     "url('/gamepictures/truthbackground.webp') center/cover no-repeat",
  lightDare: "url('/gamepictures/lightdarebackground.webp') center/cover no-repeat",
  dare:      "url('/gamepictures/darebackground.webp') center/cover no-repeat",
  kinkyDare: 'linear-gradient(to bottom,#1a0020,#2d0035,#0d0015)',
};

// Must match GetCardBorderColor.js exactly so the wrapper glow == DeeperCard glow
const BACK_BORDER = {
  truth:     'rgba(180, 60, 160, 0.5)',
  lightDare: 'rgba(210, 55, 90, 0.5)',
  dare:      'rgba(210, 60, 30, 0.5)',
  kinkyDare: 'rgba(220, 20, 20, 0.55)',
};

export async function animateCardFlip(cardImage, cardType, startRect, fetchFn) {
  injectKeyframes();

  const ar = window.innerWidth / window.innerHeight;
  const bgImage = ar >= 16 / 9
    ? '/gamepictures/backgroundsixteenonnine.webp'
    : ar >= 4 / 3
      ? '/gamepictures/backgroundfouronthree.webp'
      : '/gamepictures/backgroundphone.webp';

  /* scrim — added AFTER the page has faded so it's always fully opaque on entry */
  const scrim = document.createElement('div');
  scrim.style.cssText =
    'position:fixed;top:0;left:0;right:0;bottom:0;' +
    `background:url('${bgImage}') center/cover no-repeat,#0C0717;` +
    'z-index:1100;pointer-events:all;';

  /* wrapper — starts with the same shadow as DareButton so appearance is seamless */
  const wrapper = document.createElement('div');
  wrapper.style.cssText =
    `position:fixed;top:${startRect.top}px;left:${startRect.left}px;` +
    `width:${startRect.width}px;height:${startRect.height}px;` +
    'transform-style:preserve-3d;-webkit-transform-style:preserve-3d;' +
    'z-index:1101;border-radius:12px;' +
    'box-shadow:0px 6px 16px rgba(0,0,0,0.55);';

  /* front face */
  const front = document.createElement('div');
  front.style.cssText =
    'position:absolute;inset:0;border-radius:inherit;overflow:hidden;' +
    'backface-visibility:hidden;-webkit-backface-visibility:hidden;';
  const img = document.createElement('img');
  img.src = cardImage;
  img.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;';
  front.appendChild(img);

  /* back face */
  const borderCol = BACK_BORDER[cardType] || 'rgba(255,191,229,0.3)';
  const backBg    = BACK_BG[cardType]     || '#0C0717';

  const back = document.createElement('div');
  back.style.cssText =
    'position:absolute;inset:0;border-radius:inherit;overflow:hidden;' +
    'backface-visibility:hidden;-webkit-backface-visibility:hidden;' +
    'transform:rotateY(180deg);' +
    `background:${backBg};border:1px solid ${borderCol};`;

  // No innerRing div here — the cloned DeeperCard brings its own ::before inner ring.
  // Adding one here would double the opacity and make it visibly brighter than the real card.

  wrapper.appendChild(front);
  wrapper.appendChild(back);

  /* blocker — prevents double-taps; sits below the wrapper but above the app */
  const blocker = document.createElement('div');
  blocker.style.cssText =
    'position:fixed;top:0;left:0;right:0;bottom:0;z-index:1099;pointer-events:all;';

  const root = document.getElementById('root');
  /*
   * Save body's inline background so we can restore it.
   * We temporarily strip body's background so the <html> element's
   * background-image (same photo) shows through during the root fade.
   * This prevents the plain-CSS gradient from popping in.
   */
  const savedBodyBg = document.body.style.background || '';

  document.body.appendChild(blocker);
  document.body.appendChild(wrapper);

  try {
    /*
     * Phase 1 — smooth page fade-out.
     *
     * body.background → 'none' makes body transparent, so the <html>
     * element's photo background shows through.  As #root fades to 0,
     * the user sees the same photo rather than a mismatched CSS gradient.
     *
     * The scrim is NOT in the DOM yet, so there is zero chance of the
     * QuestionCard flashing through a semi-transparent overlay.
     */
    // No pulse during the fade — wrapper stays at static DareButton shadow
    document.body.style.background = 'none';

    if (root) {
      root.style.transition = 'opacity 0.25s ease';
      root.style.opacity    = '0';
    }

    await sleep(280); // outlast the 250 ms fade

    /*
     * Page is now invisible.  Add the opaque scrim, restore body + root,
     * then start the pulse (only visible above the scrim during the fetch wait).
     */
    document.body.appendChild(scrim); // z-index 1100, below wrapper (1101)
    document.body.style.background = savedBodyBg;

    if (root) {
      root.style.transition = 'none';
      root.style.opacity    = '';
    }

    wrapper.style.setProperty('--cf-glow', borderCol); // match glow to card type
    wrapper.classList.add('cf-pulse'); // pulse starts here, after page is hidden

    /* Phase 2 — fetch; page switch is fully covered by the opaque scrim */
    await fetchFn();
    wrapper.classList.remove('cf-pulse');
    // Set final box-shadow immediately (no animation) so there's no shadow jump
    // when the grow transition starts — the value is already at target by then.
    wrapper.style.boxShadow = `0 4px 20px rgba(0,0,0,0.4),0 0 14px ${borderCol}`;

    /* yield for React to commit QuestionCard to the DOM */
    await sleep(80);
    const realCard = document.querySelector('[data-question-card]');

    /* Phase 3 — ONE grow to the real DeeperCard position */
    let finalTop, finalLeft, finalW, finalH;
    if (realCard) {
      const r  = realCard.getBoundingClientRect();
      finalTop  = r.top;
      finalLeft = r.left;
      finalW    = r.width;
      finalH    = r.height;
    } else {
      finalW    = Math.min(500, Math.max(200, Math.min(window.innerWidth * 0.7, window.innerHeight * 0.392)));
      finalH    = finalW * (3.5 / 2.5);
      finalTop  = (window.innerHeight - finalH) / 2;
      finalLeft = (window.innerWidth  - finalW) / 2;
    }

    wrapper.getBoundingClientRect(); // force reflow
    wrapper.style.transition =
      'top 0.48s cubic-bezier(0.25,0.46,0.45,0.94),' +
      'left 0.48s cubic-bezier(0.25,0.46,0.45,0.94),' +
      'width 0.48s cubic-bezier(0.25,0.46,0.45,0.94),' +
      'height 0.48s cubic-bezier(0.25,0.46,0.45,0.94),' +
      'border-radius 0.48s ease';
    // box-shadow is NOT in the transition — already at its final value above

    wrapper.style.top          = `${finalTop}px`;
    wrapper.style.left         = `${finalLeft}px`;
    wrapper.style.width        = `${finalW}px`;
    wrapper.style.height       = `${finalH}px`;
    wrapper.style.borderRadius = '16px';

    await sleep(500);
    wrapper.style.transition = 'none';

    /* Clone the real DeeperCard onto the back face for pixel-perfect text */
    if (realCard) {
      const clone = realCard.cloneNode(true);
      clone.removeAttribute('data-question-card');
      clone.style.cssText =
        'position:absolute;top:0;left:0;right:0;bottom:0;' +
        'width:auto;height:auto;min-height:0;aspect-ratio:unset;' +
        'background:none;box-shadow:none;border:none;' +
        'border-radius:inherit;margin:0;';
      back.appendChild(clone);
    }

    /* Phase 4 — 3D flip */
    wrapper.style.transition = 'transform 1.0s cubic-bezier(0.4,0.0,0.2,1)';
    wrapper.style.transform  = 'perspective(1200px) rotateY(180deg)';
    await sleep(1020);

    /* Phase 5 — fade scrim + fade wrapper shadow simultaneously.
     * As scrim becomes transparent, DeeperCard's shadow becomes visible below it.
     * Fading the wrapper's shadow out at the same rate prevents the two shadows
     * from stacking and causing a glow burst right before the handoff. */
    wrapper.style.transition = 'box-shadow 0.18s ease';
    wrapper.style.boxShadow  = '0 4px 20px rgba(0,0,0,0), 0 0 0px rgba(0,0,0,0)';
    scrim.style.transition   = 'opacity 0.18s ease';
    scrim.style.opacity      = '0';
    await sleep(200);

    wrapper.remove(); // instant — opacity transition would collapse the 3D context

  } finally {
    if (scrim.parentNode)   scrim.remove();
    if (wrapper.parentNode) wrapper.remove();
    if (blocker.parentNode) blocker.remove();
    /* restore body and root in case of early error */
    document.body.style.background = savedBodyBg;
    if (root && root.style.opacity === '0') {
      root.style.transition = 'none';
      root.style.opacity    = '';
    }
  }
}
